import dayjs from 'dayjs';
import { Check, LoaderCircle, X } from 'lucide-react';

import { Badge } from '@/_shared/components/ui/badge';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { formatDateTimeTitle } from '@/_shared/lib/format';
import { cn } from '@/_shared/lib/utils';

import type { useWorkspaceSlug } from '../../hooks/useWorkspaceSlug';
import { RegionFlag } from '../regionFlag/RegionFlag';

export const MEMBERSHIP_SUSPENDED_MESSAGE =
  'Your access to this organization is suspended. Ask an organization admin to restore your seat in RudderStack.';

export function RegionBadge({ region }: { region: string }): React.ReactElement {
  return (
    <Badge variant="outline" className="shrink-0 gap-1.5">
      <RegionFlag region={region} />
      {region}
    </Badge>
  );
}

/** Highlighted like the workspace name, with optional detail in a tooltip. */
function HighlightedText({
  children,
  tooltip,
  className,
}: {
  children: string;
  tooltip: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const text = <span className={cn('font-medium text-foreground', className)}>{children}</span>;
  if (tooltip === null) return text;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{text}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** "Created by <name> on <date>". The name truncates, and its tooltip shows the full name and the email. */
export function CreatedByLine({
  target,
}: {
  target: { createdBy: string | null; createdByEmail: string | null; createdAt: string };
}): React.ReactElement {
  const creator = target.createdBy ?? target.createdByEmail;
  // A name that falls back to the email shows it once.
  const email =
    target.createdByEmail && target.createdByEmail.toLowerCase() !== creator?.toLowerCase()
      ? target.createdByEmail
      : null;
  const date = (
    <HighlightedText className="shrink-0" tooltip={formatDateTimeTitle(target.createdAt)}>
      {dayjs(target.createdAt).format('MMM D, YYYY')}
    </HighlightedText>
  );
  return (
    <p className="flex min-w-0 items-baseline gap-1 text-xs text-muted-foreground">
      {creator ? (
        <>
          <span className="shrink-0">Created by</span>{' '}
          <HighlightedText
            className="min-w-0 truncate"
            tooltip={
              email ? (
                <>
                  <span className="block">{creator}</span>
                  <span className="block">{email}</span>
                </>
              ) : (
                creator
              )
            }
          >
            {creator}
          </HighlightedText>
          {email && <span className="sr-only"> ({email})</span>} <span className="shrink-0">on</span> {date}
        </>
      ) : (
        <>
          <span className="shrink-0">Created on</span> {date}
        </>
      )}
    </p>
  );
}

/** The "Workspace URL" input of a create form, with the availability mark and message of its slug. */
export function WorkspaceUrlField({
  id,
  field,
  readOnly = false,
}: {
  id: string;
  field: ReturnType<typeof useWorkspaceSlug>;
  readOnly?: boolean;
}): React.ReactElement {
  return (
    <>
      <Label htmlFor={id}>Workspace URL</Label>
      <div className="mt-1.5 flex items-stretch">
        <span className="flex items-center rounded-l-md border border-r-0 border-grey-75 bg-muted px-2 text-xs text-muted-foreground">
          /w/
        </span>
        <div className="relative flex-1">
          <Input
            id={id}
            size="lg"
            value={field.slug}
            onChange={(e) => field.change(e.target.value)}
            maxLength={30}
            readOnly={readOnly}
            className="rounded-l-none pr-8"
          />
          <span className="absolute top-1/2 right-2 -translate-y-1/2">
            {field.status === 'checking' && <LoaderCircle className="size-4 animate-spin text-muted-foreground" />}
            {field.status === 'available' && <Check className="size-4 text-success" />}
            {(field.status === 'taken' || field.status === 'invalid') && <X className="size-4 text-danger" />}
          </span>
        </div>
      </div>
      {field.message && (
        <p className="mt-1 text-xs text-danger" role="alert">
          {field.message}
        </p>
      )}
    </>
  );
}
